import React from 'react';
import { ApplicationLoadError } from 'further-ui/components';
import { useLogout } from 'hooks/ui/useLogout';
import { Box, Button } from '@mui/material';
import useUser from 'hooks/ui/useUser';
import { useWhiteLabellingContext } from 'contexts/WhiteLabellingContext';
import getUserType from 'utils/getUserType';

const ImpersonationSubdomainGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const user = useUser();
  const { isSubdomain, linkedFirmIds } = useWhiteLabellingContext();
  const { userIsInvestor } = getUserType(user);
  const { logout } = useLogout();

  const isLoggedIn = user?.isAuthenticated && !user?.authGateDecision;
  const isImpersonating = user?.isAdminUserImpersonatingFurtherUser;

  const linkedFirmIdList = [
    linkedFirmIds?.eisFirmId,
    linkedFirmIds?.lpFirmId,
  ].filter((id): id is string => Boolean(id));

  const subdomainFirmMismatch =
    isSubdomain &&
    linkedFirmIdList.length > 0 &&
    ((userIsInvestor &&
      user.firmId != null &&
      !linkedFirmIdList.includes(user.firmId)) ||
      (user.restrictToFirmId != null &&
        !linkedFirmIdList.includes(user.restrictToFirmId)));

  const shouldShowWarning =
    isImpersonating && isLoggedIn && subdomainFirmMismatch;

  if (!shouldShowWarning) {
    return <>{children}</>;
  }

  const description = user.firmName
    ? `You are impersonating ${user.fullName} who belongs to ${user.firmName}. This subdomain is for a different firm.`
    : `You are impersonating ${user.fullName}, who belongs to another firm. This subdomain is for a different firm.`;

  return (
    <ApplicationLoadError
      logoPath="assets/images/logo.webp"
      title="Wrong subdomain for impersonated user"
      description={description}
      instructionsTitle="To fix this issue:"
      instructions={[
        'Navigate to the correct subdomain for this user.',
        "If that doesn't work, use the button below to log out.",
      ]}
      centerInstructions
      action={
        <Box
          display="flex"
          gap={4}
          flexDirection="column"
          justifyContent="center"
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={() => logout()}
            >
              Log out
            </Button>
          </span>
        </Box>
      }
    />
  );
};

export default ImpersonationSubdomainGate;
